/**
 * Shared piece lookup, cached via TanStack Query.
 *
 * Extracted from SocialCalendarView.tsx during FEAT-092 — `SocialPostEditor`
 * needs the parent piece for FEAT-090's rule (a `linkedin_article` row's
 * schedule belongs to `pieces.publication_date`, not to the row's own copy),
 * and the calendar grid's cards need it for their tooltips. Same shape as
 * `amplifier-lookup.ts` and `social-account-lookup.ts`, for the same reason:
 * one query key should not have two implementations.
 *
 * The key matches PieceView.tsx's own `["piece", id]`, so opening the editor
 * on the piece screen reuses the piece already in cache rather than refetching.
 */
import { useApiQuery } from "@/lib/api";

export interface PieceLookupRow {
  id: number;
  title: string;
  slug: string;
  publicationId?: number;
  publication_id?: number;
  /**
   * FEAT-090 — when the amplification publishes with its article, this is the
   * date that actually decides when it goes out. GET /pieces/:id selects the
   * whole row, so it has always been in the response; it simply was not typed
   * here. Read it rather than the social row's own scheduled_at copy: the two
   * agree today, but only one of them is the thing the publisher acts on.
   */
  publication_date?: string | null;
}

export function usePieceLookup(id: number | null) {
  const { data } = useApiQuery<PieceLookupRow>(
    ["piece", id ?? 0],
    id ? `/pieces/${id}` : "/pieces/0",
    {
      enabled: id != null,
      staleTime: 5 * 60_000,
      retry: false,
    }
  );
  return data;
}
